<template>
  <el-dialog
    ref="formModal"
    id="edit-modal"
    class="form-modal"
    v-loading="bulkEditProcessing"
    :visible.sync="visible"
    :fullscreen="true"
    :show-close="false"
    :modal="false"
  >
    <div id="edit-modal-header" class="modal-header">
      <div class="modal-header-layout flex grid-6 align-center">
        <h1 class="modal-title col-2">Bulk Edit Extensions</h1>
        <div class="modal-actions col-2">
          <el-button v-if="editEveryExt" type="primary" @click="editExtensions">
            Edit
          </el-button>
          <el-button
            v-else
            type="primary"
            :disabled="!extensionsToEdit.length"
            @click="editExtensions"
          >
            Edit
          </el-button>
          <el-button type="default" @click="cancelOperation">
            Cancel
          </el-button>
        </div>
      </div>
    </div>

    <el-form>
      <el-tabs v-model="activeTab">
        <!-- ⭐ GENERAL ⭐ -->
        <el-tab-pane label="General" name="general">
          <dads-select
            v-model="branch"
            placeholder="Select a branch"
            filterable
            clearable
          >
            <el-option
              v-for="branch of branchOptions"
              :key="branch.id"
              :value="branch.id"
              :label="branch.name"
            />
            <template slot="prefix"> Select a branch </template>
          </dads-select>
          <el-checkbox v-model="editEveryExt" v-if="branch || branch === null">
            Apply to all extensions
          </el-checkbox>
          <el-transfer
            v-if="!editEveryExt"
            ref="extTransfer"
            filterable
            v-model="extensionsToEdit"
            :data="extensionsData"
            v-loading="extensionsLoading"
            :titles="['Available', 'Assigned']"
          />
        </el-tab-pane>
        <!-- ⭐ EDIT FIELDS ⭐ -->
        <el-tab-pane
          v-if="editEveryExt || extensionsToEdit.length"
          label="Edit fields"
          name="fields"
        >
          <!-- 🆔 CALL WAITING 🆔 -->
          <el-checkbox v-model="bulkEditForm.sendInvite">Send Invite</el-checkbox>
          <el-checkbox v-model="callWaiting">Call waiting</el-checkbox>

          <dads-select
            v-model="bulkEditForm.emergencyAddress"
            placeholder="Select e911 location"
            v-loading="locationsProcessing"
            :disabled="!locationOptions.length"
            filterable
          >
            <template slot="prefix">Location</template>
            <el-option
              v-for="option in locationOptions"
              :key="option.id"
              :value="option.id"
              :label="option.name"
            />
          </dads-select>

          <!-- 🆔 BRANCH && DEPARTMENT 🆔 -->
          <div class="inline-form-group">
            <el-col :span="11">
              <dads-select
                v-model="bulkEditForm.branchId"
                placeholder="Select a branch"
                filterable
              >
                <el-option
                  v-for="(branch, index) of branchOptions"
                  :key="index"
                  :value="branch.id"
                  :label="branch.name"
                />
                <template slot="prefix">Select a branch</template>
              </dads-select>
            </el-col>
            <el-col :span="1"></el-col>
            <el-col :span="12">
              <el-input
                v-model="bulkEditForm.department"
                placeholder="Department"
              >
                <template slot="prefix">Department</template>
              </el-input>
            </el-col>
          </div>

          <!-- 🆔 OUTBOUND RECORDING 🆔 -->
          <h2 class="form-section-header">Outbound recording</h2>
          <dads-select
            v-model="bulkEditForm.recording"
            placeholder="Select option"
          >
            <template slot="prefix"> Recording </template>
            <el-option
              v-for="(item, index) in outboundRecordingOptions"
              :key="index"
              :label="item.label"
              :value="item.value"
            />
          </dads-select>
          <el-tag class="mb-1">
            <i class="el-icon-info" style="margin-right: 4px" />
            To enable inbound call recording, edit the phone numbers
          </el-tag>

          <!-- 🆔 OUTBOUND CALLER ID🆔 -->
          <h2 class="form-section-header">Outbound caller id</h2>
          <dads-select
            v-model="bulkEditForm.cidNum"
            filterable
            allow-create
            remote
            :remote-method="remoteMethodForOutboundCid"
            placeholder="Select caller id"
          >
            <template slot="prefix">Ext caller id</template>
            <el-option
              v-for="option in outboundCidNumbersOptions"
              :key="option.id"
              :value="option.label"
              :label="option.label | formatNumber"
            />
          </dads-select>

          <!-- 🆔 Outbound Faxing 🆔 -->
          <h2 class="form-section-header">Outbound Faxing</h2>
          <el-select
            v-model="bulkEditForm.outboundFaxAction"
            placeholder="Select action"
          >
            <el-option
              v-for="item in outboundFaxingOptions"
              :key="item.label"
              :value="item.value"
              :label="item.label"
            />
            <template slot="prefix">Action</template>
          </el-select>

          <div v-if="bulkEditForm.outboundFaxAction === 'enable'">
            <el-select
              v-model="bulkEditForm.faxCidNum"
              filterable
              allow-create
              clearable
              remote
              :remote-method="remoteFaxOutboundCid"
              placeholder="Select caller id"
            >
              <template slot="prefix">Fax caller id</template>
              <el-option
                v-for="option in faxOutboundCidOptions"
                :key="option.id"
                :value="option.label"
                :label="option.label | formatNumber"
              />
            </el-select>
            <el-tag class="mb-1">
              <i class="el-icon-info" style="margin-right: 4px" />
              To enable outbound faxing, you must select a caller id.
            </el-tag>
          </div>

          <!-- 🆔 DONOT CALL LIST 🆔 -->
          <h2 class="form-section-header">Do Not Calls</h2>
          <el-select
            v-model="bulkEditForm.dncId"
            placeholder="Select do not call list"
          >
            <template slot="prefix">Do not call list</template>
            <el-option
              v-for="(item, i) in donotcallList"
              :key="i"
              :value="item.id"
              :label="item.name"
            />
          </el-select>

          <h2 class="form-section-header">Additional destinations</h2>
          <destination-selector
            title="Destination"
            :show-header="false"
            :clearFields="clearDestinationFields"
            :allowVMSameNumber="true"
            @destinationSelected="onDestSelection"
          />
          <el-tag class="mb-10">
            <i class="el-icon-info" style="margin-right: 4px"></i>
            Assign unavailable, busy, and offline destinations
          </el-tag>
        </el-tab-pane>
      </el-tabs>
    </el-form>
  </el-dialog>
</template>

<script>
import { mapActions } from "vuex";
import feathersClient from "@/feathers/feathers-client";
import DadsSelect from "@/components/select";
import DestinationSelector from "@/components/DestinationSelector.vue";
import {phoneNumberFormatter, isValidPhoneNumber} from "@/globals/globals";

export default {
  name: "bulkEditExtensions",

  props: {
    visible: { required: true },
    departmentOptions: { required: true },
    outboundCidNumbersOptions: { required: true },
    faxOutboundCidOptions: { required: true },
    locationOptions: { required: true },
    locationsProcessing: { required: true },
    donotcallList: { required: true },
    branchOptions: { required: true },
    outboundRecordingOptions: { required: true },
  },

  components: { DestinationSelector, DadsSelect },

  data() {
    return {
      clearDestinationFields: false,
      activeTab: "general",
      extensions: [],
      extensionsData: [],
      extensionsToEdit: [],
      extensionsLoading: false,
      bulkEditProcessing: false,
      branch: "",
      editEveryExt: false,
      dstOptions: [],
      callWaiting: false,
      bulkEditForm: {
        dstId: null,
        dstType: "",
        branchId: null,
        department: "",
        emergencyAddress: null,
        cidNum: "",
        callWaiting: false,
        dncId: null,
        outboundFaxAction: null,
        faxCidNum: "",
        recording: "",
        sendInvite: false,
      },
      outboundFaxingOptions: [
        { label: "No Change", value: "" },
        { label: "Enable", value: "enable" },
        { label: "Disable", value: "disable" },
      ],
    };
  },

  computed: {
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },

    dstTypeSelectorLabels() {
      return this.$store.state.dstTypeSelectorLabels;
    },
  },

  watch: {
    branch(newVal) {
      if (!newVal && newVal !== null) {
        return;
      }

      this.extensionsLoading = true;
      feathersClient
        .service("extensions")
        .find({
          query: {
            queryPhonebook: true,
            tenantId: this.selectedTenant.id,
            branchId: newVal,
          },
        })
        .then((res) => {
          this.extensionsLoading = false;
          this.extensions = res;
        })
        .catch(() => {
          this.extensionsLoading = false;
          this.extensions = [];
        });
    },

    extensions(newVal) {
      this.extensionsData = [];
      if (newVal.length) {
        for (let i = 0; i < newVal.length; i++) {
          const { name, number, id } = newVal[i];
          this.extensionsData.push({
            key: id,
            label: `${name ? name : ""}${number ? "-" + number : ""}`,
            disabled: false,
          });
        }
      }
    },

    visible(val) {
      if (val) {
        this.clearDestinationFields = false;
      }
    },
  },

  methods: {
    ...mapActions("extensions", { patchExtensions: "patch" }),

    remoteFaxOutboundCid(query) {
      this.$emit("remoteFaxOutboundCid", query);
    },

    remoteMethodForOutboundCid(query) {
      this.$emit("remoteMethodForOutboundCid", query);
    },

    editExtensions() {
      // Validate fax outbound caller id is a valid phone number
      if (this.bulkEditForm.outboundFaxAction === 'enable' && this.bulkEditForm.faxCidNum) {
        if (!isValidPhoneNumber(this.bulkEditForm.faxCidNum)) {
          this.$notify({
            title: "Invalid Fax Caller ID",
            message: "Fax outbound caller id must be a valid phone number.",
            type: "error",
          });
          return;
        }
      }

      this.$confirm("Process Bulk Extensions Edit?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning",
      })
        .then(async () => {
          this.bulkEditProcessing = true;

          const editBody = {
            tenantId: this.selectedTenant.id,
            ...this.bulkEditForm,
            bulkPatch: true,
          };

          Object.keys(editBody).forEach((key) => {
            if (!editBody[key]) {
              if (!(key === "dstId" && editBody[key] === 0)) {
                delete editBody[key];
              }
            }
          });

          editBody.callWaiting = editBody.callWaiting ? "yes" : "";

          if (editBody.outboundFaxAction === "") {
            delete editBody.outboundFaxAction;
          }

          if (editBody.outboundFaxAction !== "enable") {
            delete editBody.faxCidNum;
          }

          if (this.editEveryExt) {
            for (let i = 0; i < this.extensions.length; i++) {
              const { id } = this.extensions[i];
              try {
                await this.patchExtensions([id, editBody]);
              } catch (err) {
                console.log({ err });
              }
            }
          } else if (this.extensionsToEdit.length) {
            for (let i = 0; i < this.extensionsToEdit.length; i++) {
              try {
                await this.patchExtensions([
                  this.extensionsToEdit[i],
                  editBody,
                ]);
              } catch (err) {
                console.log({ err });
              }
            }
          }
          this.clearDestinationFields = true;
          this.bulkEditProcessing = false;
          this.cancelOperation();

          this.$notify({
            type: "Success",
            message: "Bulk Edited Successfully!",
            type: "success",
          });
        })
        .catch(() => true);
    },

    clearFields() {
      for (let key in this.$data) {
        if (key === "outboundFaxingOptions") {
          continue;
        }

        if (typeof this.$data[key] === "string") {
          this.$data[key] = "";
        } else if (typeof this.$data[key] === "boolean") {
          this.$data[key] = false;
        } else if (Array.isArray(this.$data[key])) {
          this.$data[key] = [];
        }
      }

      this.activeTab = "general";
      this.branch = "";

      // ⭐️ Clear Query on Transfer ⭐️ //
      if (this.$refs.extTransfer) {
        this.$refs.extTransfer.clearQuery("left");
        this.$refs.extTransfer.clearQuery("right");
      }

      this.bulkEditForm = {
        dstId: null,
        dstType: "",
        branchId: null,
        department: "",
        emergencyAddress: null,
        cidNum: "",
        callWaiting: false,
        dncId: null,
        outboundFaxAction: null,
        faxCidNum: "",
        recording: "",
        sendInvite: false,
      };
    },

    cancelOperation() {
      this.$emit("modalClosed");
      this.$emit("bulkPatchComplete");
      this.clearFields();
    },

    onDestSelection(destination) {
      this.bulkEditForm.dstType = destination.type;
      this.bulkEditForm.dstId = destination.id;
    },
  },

  beforeDestroy() {
    this.$refs.formModal.$destroy();
  },

  filters: {
    formatNumber(value) {
      return phoneNumberFormatter(value);
    },
  },
};
</script>

<style lang="scss" scoped>
.el-tabs {
  max-width: 600px;
  margin: auto;
}

.el-checkbox {
  max-width: 600px;
  margin: 0 auto;
}
</style>
